import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { queryClient } from "./lib/queries";
import { routeTree } from "./routeTree.gen";
import { ReportError } from "./components/report-error";
import "./styles.css";

// Search params travel as plain strings (?from=2026-09-21&tab=campaigns), the
// way the report pages read them, instead of the router's JSON encoding.
function stringifySearch(search: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(search)) {
    if (v === undefined || v === null || v === "") continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

const router = createRouter({
  routeTree,
  context: { queryClient },
  // Hovering a link loads its route module and prefetches its page data.
  defaultPreload: "intent",
  // TanStack Query owns caching.
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  // A page that throws shows a retry inside the shell instead of a blank page.
  defaultErrorComponent: ReportError,
  parseSearch: (searchStr) => Object.fromEntries(new URLSearchParams(searchStr)),
  stringifySearch,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
